import React, { useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Alert,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type {
  OpportunityRelatedDeal,
  OpportunityRelatedInclusion,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import getErrorMessage from '~/utils/getErrorMessage';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';

import {
  buildInclusionsViewModel,
  getInclusionCurrency,
  getInclusionVenues,
  type InclusionDealRow,
  type InclusionGroup,
} from './inclusionsModel';
import { formatDealOptionCurrency, formatDealOptionValue } from '../dealOptions/dealOptionsFormatters';

interface Props {
  deals: Array<OpportunityRelatedDeal>;
  error?: unknown;
  inclusions: Array<OpportunityRelatedInclusion>;
  isLoading: boolean;
  manageInclusionsPath?: string;
}

function InclusionRows({
  deal,
  currency,
}: {
  currency: string;
  deal: InclusionDealRow;
}) {
  if (deal.inclusions.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={6}>
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No inclusions are linked to this deal option.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {deal.inclusions.map((inclusion, inclusionIndex) => {
        const inclusionKey = `${deal.id}:inclusion:${inclusion.id || inclusionIndex}`;
        const inclusionCurrency = getInclusionCurrency(inclusion, currency);
        const finePrint = inclusion.inclusion_description__c ?? inclusion.description__c;

        return (
          <TableRow key={inclusionKey} hover>
            <TableCell sx={{ minWidth: 160 }}>{formatDealOptionValue(inclusion.name)}</TableCell>
            <TableCell align="right">
              {formatDealOptionCurrency(inclusionCurrency, inclusion.rrp__c)}
            </TableCell>
            <TableCell align="right">
              {formatDealOptionCurrency(inclusionCurrency, inclusion.total_value__c)}
            </TableCell>
            <TableCell sx={{ minWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {formatDealOptionValue(finePrint)}
            </TableCell>
            <TableCell>{formatDealOptionValue(inclusion.type__c)}</TableCell>
            <TableCell>{formatDealOptionValue(getInclusionVenues(inclusion))}</TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

function InclusionDetails({ deal }: { deal: InclusionDealRow }) {
  const currency = deal.deal.currencyisocode ?? 'AUD';

  return (
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 900, tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 160 }}>Inclusion name</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              RRP
            </TableCell>
            <TableCell align="right" sx={{ width: 140 }}>
              Total value
            </TableCell>
            <TableCell sx={{ minWidth: 220 }}>Fine print</TableCell>
            <TableCell sx={{ width: 120 }}>Type</TableCell>
            <TableCell sx={{ width: 160 }}>Venues</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <InclusionRows currency={currency} deal={deal} />
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function GroupRow({
  expanded,
  group,
  onToggle,
}: {
  expanded: boolean;
  group: InclusionGroup;
  onToggle: () => void;
}) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <TableRow
      hover
      aria-expanded={expanded}
      sx={{ cursor: 'pointer', '& .MuiTableCell-root': { bgcolor: 'grey.50' } }}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
    >
      <TableCell sx={{ width: 56 }}>{expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
      <TableCell colSpan={2}>
        <Typography fontWeight={700} variant="body2">
          {group.roomName}{' '}
          <Typography color="text.secondary" component="span" variant="body2">
            ({group.deals.length} {group.deals.length === 1 ? 'deal option' : 'deal options'})
          </Typography>
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function DealRow({
  expanded,
  group,
  onToggle,
}: {
  expanded: boolean;
  group: InclusionDealRow;
  onToggle: () => void;
}) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <>
      <TableRow
        hover
        aria-expanded={expanded}
        sx={{ cursor: 'pointer' }}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
      >
        <TableCell />
        <TableCell sx={{ minWidth: 240 }}>
          {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}{' '}
          {group.name}
        </TableCell>
        <TableCell align="right">{group.inclusions.length}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ borderBottom: expanded ? 1 : 0, borderColor: 'divider', p: 0 }}>
          <Collapse unmountOnExit in={expanded} timeout="auto">
            <Stack sx={{ bgcolor: 'action.hover', borderLeft: 4, borderLeftColor: 'divider', pl: 2, pr: 3, py: 2 }}>
              <InclusionDetails deal={group} />
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function InclusionsTable({
  expandedGroups,
  expandedDeals,
  groups,
  onToggleDeal,
  onToggleGroup,
}: {
  expandedDeals: Set<string>;
  expandedGroups: Set<string>;
  groups: Array<InclusionGroup>;
  onToggleDeal: (dealId: string) => void;
  onToggleGroup: (groupId: string) => void;
}) {
  return (
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 56 }} />
            <TableCell>Room type / deal option</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              Inclusions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const isGroupExpanded = expandedGroups.has(group.id);

            return (
              <React.Fragment key={group.id}>
                <GroupRow expanded={isGroupExpanded} group={group} onToggle={() => onToggleGroup(group.id)} />
                {isGroupExpanded &&
                  group.deals.map((deal) => {
                    const dealExpansionId = `${group.id}:${deal.id}`;

                    return (
                      <DealRow
                        key={deal.id}
                        expanded={expandedDeals.has(dealExpansionId)}
                        group={deal}
                        onToggle={() => onToggleDeal(dealExpansionId)}
                      />
                    );
                  })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function InclusionsTab({ deals, error, inclusions, isLoading, manageInclusionsPath }: Props) {
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const viewModel = useMemo(() => buildInclusionsViewModel({ deals, inclusions }), [deals, inclusions]);
  const allGroupIds = useMemo(() => viewModel.groups.map((group) => group.id), [viewModel.groups]);
  const allGroupsExpanded = allGroupIds.length > 0 && expandedGroups.size === allGroupIds.length;
  useDealInformationTabCount('inclusions', isLoading || error ? undefined : inclusions.length);

  const toggleAllGroups = () => {
    setExpandedGroups((currentGroups) =>
      currentGroups.size === allGroupIds.length ? new Set() : new Set(allGroupIds),
    );
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((currentGroups) => {
      const nextGroups = new Set(currentGroups);
      if (nextGroups.has(groupId)) {
        nextGroups.delete(groupId);
      } else {
        nextGroups.add(groupId);
      }
      return nextGroups;
    });
  };

  const toggleDeal = (dealId: string) => {
    setExpandedDeals((currentDeals) => {
      const nextDeals = new Set(currentDeals);
      if (nextDeals.has(dealId)) {
        nextDeals.delete(dealId);
      } else {
        nextDeals.add(dealId);
      }
      return nextDeals;
    });
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading inclusions.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load Salesforce inclusions.')}</Alert>;
  }

  const isEmpty = inclusions.length === 0;
  const subtitle = `${viewModel.groups.length} room ${
    viewModel.groups.length === 1 ? 'type' : 'types'
  } · ${inclusions.length} ${inclusions.length === 1 ? 'inclusion' : 'inclusions'}`;

  return (
    <TabPanelShell
      actions={
        <>
          {manageInclusionsPath && (
            <Button component={Link} size="small" to={manageInclusionsPath} variant="outlined">
              Manage content inclusions
            </Button>
          )}
          <Button
            disabled={allGroupIds.length === 0}
            size="small"
            startIcon={allGroupsExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            sx={{ textTransform: 'none' }}
            variant="outlined"
            onClick={toggleAllGroups}
          >
            {allGroupsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </>
      }
      contentSx={{ p: 0 }}
      subtitle={subtitle}
      title="Inclusions"
    >
      {isEmpty ? (
        <Typography color="text.secondary" sx={{ px: 3, py: 5, textAlign: 'center' }}>
          No Salesforce inclusions found for this offer.
        </Typography>
      ) : viewModel.groups.length === 0 ? (
        <Typography color="text.secondary" sx={{ px: 3, py: 5, textAlign: 'center' }}>
          No deal options are available to group these inclusions.
        </Typography>
      ) : (
        <InclusionsTable
          expandedDeals={expandedDeals}
          expandedGroups={expandedGroups}
          groups={viewModel.groups}
          onToggleDeal={toggleDeal}
          onToggleGroup={toggleGroup}
        />
      )}
    </TabPanelShell>
  );
}
